import * as React from 'react';
import {
  Button,
  Dropdown,
  Form,
  MenuProps,
  AutoComplete,
  Space, // Used for layout
} from 'antd';
import { DeleteOutlined, CloseOutlined, TagOutlined, DownOutlined } from '@ant-design/icons';
import { SpeakerInfo } from '@gen/proto/lr/transcript/v1alpha1/transcript_pb';
import './SpeakerModal.css';

interface Props {
  onCancel: () => void;
  onRemove: () => void;
  onEdit: (speaker: SpeakerInfo.AsObject, id: string) => void;
  onSubmit: (speaker: SpeakerInfo.AsObject, id?: string) => void;
  speakersMap: Array<[string, SpeakerInfo.AsObject]>;
  speakerForEdit?: [string, SpeakerInfo.AsObject];
}

interface State {
  ageGroup: SpeakerInfo.AgeGroup;
  editing: boolean;
  id?: string;
  fullName: string;
  gender: SpeakerInfo.Gender;
}

class SpeakerModal extends React.Component<Props, State> {
  private nameInputRef: React.RefObject<any>;

  constructor(props: Props) {
    super(props);

    this.state = {
      ageGroup: 0,
      editing: false,
      fullName: '',
      gender: 0,
    };

    this.nameInputRef = React.createRef<any>();
  }

  componentDidMount = () => {
    const { speakerForEdit } = this.props;
    if (speakerForEdit) {
      this.setState({
        editing: true,
        ...speakerForEdit[1],
        id: speakerForEdit[0],
      });
    }

    if (this.nameInputRef.current) {
      this.nameInputRef.current.focus();
    }

    document.addEventListener('keydown', this.onKeyDown);
  };

  componentWillUnmount = () => {
    document.removeEventListener('keydown', this.onKeyDown);
  };

  onKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    if (key === 'Enter') {
      this.onSubmit();
    } else if (key === 'Escape') {
      this.props.onCancel();
    }
  };

  ageGroupToString = (ageGroup: SpeakerInfo.AgeGroup): string => {
    switch (ageGroup) {
      case 1:
        return 'Barn';
      case 2:
        return 'Unglingur';
      case 3:
        return 'Fullorðinn';
      case 4:
        return 'Aldraður';
      default:
        return 'Ótilgreindur';
    }
  };

  genderToString = (gender: SpeakerInfo.Gender): string => {
    switch (gender) {
      case 1:
        return 'Karlkyns';
      case 2:
        return 'Kvenkyns';
      case 3:
        return 'Annað';
      default:
        return 'Ótilgreint';
    }
  };

  onNameChange = (fullName: string) => {
    this.setState({ fullName });

    const speaker = this.props.speakersMap.find(([_, speaker]) => speaker.fullName === fullName);
    if (speaker) {
      const [id, value] = speaker;
      this.setState({
        ...value,
        id,
      });
    } else {
      // Not a match, reset other fields but keep the typed name
      this.setState({
        id: undefined,
        fullName: fullName,
        ageGroup: 0,
        gender: 0,
      });
    }
  };

  onSpeakerSelect = (fullName: string) => {
    const speaker = this.props.speakersMap.find(([_, speaker]) => speaker.fullName === fullName);
    if (speaker) {
      const [id, value] = speaker;
      this.setState({ ...value, id, fullName });
    }
  };

  onSubmit = () => {
    const { ageGroup, fullName, editing, gender, id } = this.state;
    const speaker = {
      ageGroup,
      fullName,
      gender,
    };
    if (editing && id && fullName) {
      this.props.onEdit(speaker, id);
    } else {
      if (!fullName) {
        return;
      }
      this.props.onSubmit(speaker, id);
    }
  };

  render() {
    const { speakersMap, onCancel, onRemove } = this.props;
    const { ageGroup, editing, gender, fullName } = this.state;

    const speakerOptions = speakersMap.map(([_, speaker]) => ({
      value: speaker.fullName,
    }));

    const ageMenu: MenuProps = {
      items: [0, 1, 2, 3, 4].map((i) => ({
        key: i,
        label: this.ageGroupToString(i),
      })),
      selectable: true,
      selectedKeys: [ageGroup.toString()],
      onClick: ({ key }) => this.setState({ ageGroup: Number(key) }),
    };

    const genderMenu: MenuProps = {
      items: [0, 1, 2, 3].map((i) => ({
        key: i,
        label: this.genderToString(i),
      })),
      selectable: true,
      selectedKeys: [gender.toString()],
      onClick: ({ key }) => this.setState({ gender: Number(key) }),
    };

    return (
      <div className={'speaker-modal-container'}>
        <div className={'speaker-modal-inner'}>
          <div className={'speaker-modal header'}>
            <h2>{editing ? 'Breyta' : 'Velja'} mælanda</h2>
            {editing && (
              <Button danger icon={<DeleteOutlined />} onClick={onRemove}>
                Eyða
              </Button>
            )}
          </div>

          <Form layout='vertical' style={{ width: '100%', maxWidth: 'none' }}>
            <Form.Item label='Nafn'>
              <AutoComplete
                ref={this.nameInputRef}
                options={speakerOptions}
                style={{ width: '100%' }}
                value={fullName}
                onChange={this.onNameChange}
                onSelect={this.onSpeakerSelect}
                filterOption={(inputValue, option) =>
                  option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                }
              />
            </Form.Item>

            <Form.Item label='Aldur'>
              <Dropdown menu={ageMenu}>
                <Button block style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {this.ageGroupToString(ageGroup)}
                  <DownOutlined />
                </Button>
              </Dropdown>
            </Form.Item>

            <Form.Item label='Kyn'>
              <Dropdown menu={genderMenu}>
                <Button block style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  {this.genderToString(gender)}
                  <DownOutlined />
                </Button>
              </Dropdown>
            </Form.Item>
          </Form>

          <div className='submit'>
            <Space>
              <Button size={'large'} icon={<CloseOutlined />} onClick={onCancel}>
                Hætta við
              </Button>
              <Button size={'large'} type='primary' icon={<TagOutlined />} onClick={this.onSubmit}>
                {editing ? 'Breyta' : 'Merkja'}
              </Button>
            </Space>
          </div>
        </div>
      </div>
    );
  }
}

export default React.memo(SpeakerModal);
